import 'swiper/css';

import type {ComponentType} from 'react';
import {useCallback, useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {useLocation, useNavigationType} from 'react-router';
import type {Swiper as SwiperInstance} from 'swiper';
import {A11y} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';

import BadgeDetailDrawerConnected from '@/components/BadgeDetailDrawer/BadgeDetailDrawerConnected';
import ScrollableSlide from '@/components/ScrollableSlide/ScrollableSlide';
import APP_ENTRIES from '@/consts/appEntries';
import NAVIGATION_TYPES from '@/consts/navigationTypes';
import ROUTE_PATHS from '@/consts/routePaths';
import BadgesPage from '@/containers/BadgesPage/BadgesPage';
import InProgressPage from '@/containers/InProgressPage/InProgressPage';
import RewardsPage from '@/containers/RewardsPage/RewardsPage';
import {selectActiveModule} from '@/state/selectors/app';

const SLIDE_MS = 300;

type SlidePageProps = {isActive: boolean};

const BADGES_ROUTES: Array<{path: string; component: ComponentType<SlidePageProps>}> = [
    {path: ROUTE_PATHS.BADGES, component: BadgesPage},
    {path: ROUTE_PATHS.IN_PROGRESS, component: InProgressPage},
];

const REWARDS_ROUTES: Array<{path: string; component: ComponentType<SlidePageProps>}> = [
    {path: ROUTE_PATHS.REWARDS, component: RewardsPage},
];

const getSlideIndex = (routes: typeof BADGES_ROUTES, pathname: string): number => {
    const idx = routes.findIndex((r) => r.path === pathname);
    return idx >= 0 ? idx : 0;
};

const AppRoutes = () => {
    const activeEntry = useSelector(selectActiveModule);
    const routes = activeEntry === APP_ENTRIES.REWARDS ? REWARDS_ROUTES : BADGES_ROUTES;

    const location = useLocation();
    const navigationType = useNavigationType();
    const swiperRef = useRef<SwiperInstance | null>(null);
    const prevPathnameRef = useRef(location.pathname);

    const onSwiper = useCallback((s: SwiperInstance) => {
        swiperRef.current = s;
    }, []);

    // Guard against A11y (or anything else) driving the slide away from the active route.
    // When focus returns to an element in an inactive slide after the popup closes,
    // A11y calls slideTo() — this corrects it back silently.
    const onSlideChange = useCallback(
        (swiper: SwiperInstance) => {
            const expected = getSlideIndex(routes, location.pathname);
            if (swiper.activeIndex !== expected) {
                swiper.slideTo(expected, 0);
            }
        },
        [routes, location.pathname]
    );

    // Computed once at mount — Swiper only uses initialSlide on first init.
    // Passing a changing value would risk Swiper re-applying it on re-renders.
    const initialSlideRef = useRef(getSlideIndex(routes, location.pathname));

    useEffect(() => {
        const prev = prevPathnameRef.current;
        const current = location.pathname;

        // Only animate when the top-level pathname actually changes (ignore search/hash).
        if (current === prev) return;
        prevPathnameRef.current = current;

        const swiper = swiperRef.current;
        if (!swiper) return;

        // REPLACE navigations are silent (e.g. initial redirect to /badges).
        const speed = navigationType === NAVIGATION_TYPES.REPLACE ? 0 : SLIDE_MS;
        swiper.slideTo(getSlideIndex(routes, current), speed);
    }, [routes, location.pathname, navigationType]);

    return (
        <>
            <div className='min-h-0 w-full flex-1 overflow-hidden'>
                <Swiper
                    className='h-full w-full'
                    modules={[A11y]}
                    slidesPerView={1}
                    spaceBetween={0}
                    speed={SLIDE_MS}
                    allowTouchMove={false}
                    initialSlide={initialSlideRef.current}
                    onSwiper={onSwiper}
                    onSlideChange={onSlideChange}
                >
                    {routes.map(({path, component: Page}) => (
                        <SwiperSlide key={path} className='h-full'>
                            {({isActive}) => (
                                <div
                                    className='h-full'
                                    aria-hidden={!isActive || undefined}
                                    inert={!isActive || undefined}
                                >
                                    <ScrollableSlide>
                                        <Page isActive={isActive} />
                                    </ScrollableSlide>
                                </div>
                            )}
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
            <BadgeDetailDrawerConnected />
        </>
    );
};

export default AppRoutes;
